import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image'
import React, { useMemo } from 'react'

import {
  Maybe,
  SanityContextBasedContent,
  SanityImage,
  SanityLink as SanityLinkType,
  SanityRoleBasedContent,
} from '../../graphql/gatsby'
import { setAnalyticsEvent } from '../../lib/analytics/state'
import { useSanityRoleAndContextBasedContent } from '../../lib/sanity'
import { getGatsbyImageDataForAsset } from '../../utils/sanity/image'
import SanityLink, { NormalizedSanityLinkTagged } from '../global/SanityLink'

interface SingleBannerImageProps {
  image?: Maybe<SanityImage>
  imageAlt?: Maybe<string>
  link?: Maybe<SanityLinkType>
  roleBasedContent?: Maybe<SanityRoleBasedContent>
  contextBasedContent?: Maybe<SanityContextBasedContent>
}

const setAnalyticsEventOnClick = (imageAlt: string, link: NormalizedSanityLinkTagged) => {
  setAnalyticsEvent({
    event: 'select_content',
    content_type: 'single_banner_image',
    item_id: 'single-banner-image',
    title: imageAlt || 'Banner Image',
    label_text: imageAlt || 'Banner Image',
    link_destination: link._tag === 'Normalized' ? link.href ?? undefined : link._tag,
    module_name: 'SingleBannerImage',
  })
}

const SingleBannerImage = ({
  image,
  imageAlt,
  link,
  roleBasedContent,
  contextBasedContent,
}: SingleBannerImageProps) => {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const shouldDisplay = useMemo(
    () => shouldDisplayContent({ contextBasedContent, roleBasedContent }),
    [contextBasedContent, roleBasedContent, shouldDisplayContent],
  )

  const imgElt = useMemo((): JSX.Element | undefined => {
    const mobileImageData = getGatsbyImageDataForAsset(image, {
      width: 576,
      placeholder: 'blurred',
    })
    const tabletImageData = getGatsbyImageDataForAsset(image, {
      width: 991,
      placeholder: 'blurred',
    })
    const desktopImageData = getGatsbyImageDataForAsset(image, {
      width: 1920,
      placeholder: 'blurred',
    })

    const images =
      mobileImageData && tabletImageData && desktopImageData
        ? withArtDirection(desktopImageData, [
            {
              image: mobileImageData,
              media: '(max-width: 576px)',
            },
            {
              image: tabletImageData,
              media: '(max-width: 991px)',
            },
            {
              image: desktopImageData,
              media: '(min-width: 992px)',
            },
          ])
        : desktopImageData

    return images ? (
      <GatsbyImage image={images} loading="eager" alt={imageAlt || ''} className="w-full" />
    ) : undefined
  }, [image, imageAlt])

  if (!shouldDisplay) return null

  if (!imgElt) return null

  // If there's a link, wrap the image
  if (link?.target && link.target.length > 0) {
    return (
      <SanityLink
        to={link}
        onClick={(_e, normalized) => setAnalyticsEventOnClick(imageAlt || '', normalized)}
      >
        {imgElt}
      </SanityLink>
    )
  }

  // No link, just render the image
  return <>{imgElt}</>
}

export default SingleBannerImage
